import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, signal, computed, WritableSignal, Signal, ViewChild, AfterViewInit, OnDestroy, ChangeDetectorRef, effect } from '@angular/core';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AddDocsDialogComponent } from './add-docs-dialog/add-docs-dialog.component';
import { OrdersService } from '../services/entities/orders.service';
import { SupplyOrder } from '@app/types';
import { Factura, StatusHistory } from '@app/types';
import { Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject, Observable, startWith, map } from 'rxjs';
import { takeUntil, tap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MaterialModule } from '@app/material.module';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { StatusTranslatorService } from '@app/services/utils/status-translator.service';

@Component({
    selector: 'app-orders',
    standalone: true,
    imports: [CommonModule, MaterialModule, FormsModule, ReactiveFormsModule, ClipboardModule],
    providers: [DatePipe],
    templateUrl: './orders.component.html',
    styleUrls: ['./orders.component.scss'],
})
export class OrdersComponent implements OnInit, AfterViewInit, OnDestroy {
    supplyOrdersSignal: Signal<SupplyOrder[]>;
    filteredOrdersSignal: Signal<SupplyOrder[]>;

    // Combined Loading State
    apiLoadingSignal: Signal<boolean>;
    isFilteringSignal: WritableSignal<boolean> = signal(false);
    isLoadingSignal: Signal<boolean>;

    // Dynamic status list from StatusTranslatorService
    estatusDisponibles: Signal<string[]>;

    // Filtros Avanzados
    filtroForm: FormGroup;
    filteredClientes$: Observable<string[]>;
    filteredRfcs$: Observable<string[]>;
    filtrosValuesSignal: WritableSignal<any> = signal({});

    private allClientesSignal = computed(() => {
        const orders = this.supplyOrdersSignal();
        const clientes = orders.map(o => o.factura?.receptorNombre).filter(Boolean) as string[];
        return [...new Set(clientes)].sort();
    });

    private allRfcsSignal = computed(() => {
        const orders = this.supplyOrdersSignal();
        const rfcs = orders.map(o => o.clienteRFC).filter(Boolean) as string[];
        return [...new Set(rfcs)].sort();
    });

    // --- SEÑALES PARA PAGINACIÓN ---
    @ViewChild(MatPaginator) paginator!: MatPaginator; // Puede referenciar al superior o inferior
    public pageEventSignal: WritableSignal<PageEvent> = signal<PageEvent>({ pageIndex: 0, pageSize: 20, length: 0 });
    totalOrdenesFiltradasSignal: Signal<number>;
    ordersPaginadosSignal: Signal<SupplyOrder[]>;
    private destroy$ = new Subject<void>();

    // Definición de los pasos para el cálculo del progreso
    private readonly remisionSteps = { Aprobada: 1, Validada: 2 };
    private facturaStepsSignal = computed(() => {
        const statuses = this.estatusDisponibles();
        return statuses.reduce((acc: { [key: string]: number }, status: string, index: number) => {
            acc[status] = index + 1;
            return acc;
        }, {} as { [key: string]: number });
    });

    constructor(
        private customMessage: CustomMessageService,
        private customSnackBar: CustomSnackBarService,
        private dialog: MatDialog,
        private ordersService: OrdersService,
        private router: Router,
        private datePipe: DatePipe,
        private cdr: ChangeDetectorRef,
        private fb: FormBuilder,
        private statusTranslator: StatusTranslatorService
    ) {
        this.filtroForm = this.fb.group({
            textoGeneral: [''],
            cliente: [''],
            rfc: [''],
            estatus: ['']
        });

        // Initialize dynamic status list from StatusTranslatorService
        this.estatusDisponibles = computed(() => {
            const statuses = this.statusTranslator.getStatuses()();
            return statuses
                .filter(s => !s.deleted)
                .sort((a, b) => a.order - b.order)
                .map(s => s.status);
        });

        this.filteredClientes$ = this.filtroForm.get('cliente')!.valueChanges.pipe(
            startWith(''),
            map(value => this._filterList(value || '', this.allClientesSignal() || []) || [])
        );

        this.filteredRfcs$ = this.filtroForm.get('rfc')!.valueChanges.pipe(
            startWith(''),
            map(value => this._filterList(value || '', this.allRfcsSignal() || []) || [])
        );

        this.supplyOrdersSignal = this.ordersService.supplyOrdersSignal;
        this.apiLoadingSignal = this.ordersService.isLoadingSignal;

        // Combinamos carga de API y procesamiento local
        this.isLoadingSignal = computed(() => this.apiLoadingSignal() || this.isFilteringSignal());

        // Suscripción reactiva a cambios en el formulario con Debounce y Feedback Visual
        this.filtroForm.valueChanges.pipe(
            tap(() => this.isFilteringSignal.set(true)), // 1. Activar spinner
            debounceTime(400),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
            takeUntil(this.destroy$)
        ).subscribe((values) => {
            // 2. Usar setTimeout para dar tiempo al renderizado del spinner
            setTimeout(() => {
                this.filtrosValuesSignal.set(values);
                if (this.pageEventSignal().pageIndex !== 0) {
                    this.pageEventSignal.update(curr => ({ ...curr, pageIndex: 0 }));
                }
                this.isFilteringSignal.set(false); // 3. Desactivar spinner
            }, 100);
        }, (error) => {
            console.error('Error en suscripción de filtros:', error);
            this.isFilteringSignal.set(false); // Asegurar que el spinner se desactive en caso de error
        });

        this.filteredOrdersSignal = computed(() => {
            const orders = this.supplyOrdersSignal() || [];
            const filtros = this.filtrosValuesSignal();

            const searchTerm = (filtros.textoGeneral || '').toLowerCase().trim();
            const clienteFilter = (filtros.cliente || '').toLowerCase().trim();
            const rfcFilter = (filtros.rfc || '').toLowerCase().trim();
            const statusFilter = filtros.estatus;

            return orders.filter((order) => {
                // Filtro por Estatus
                if (statusFilter && (!order.factura || order.factura.estatus !== statusFilter)) {
                    return false;
                }

                // Filtro por Cliente
                if (clienteFilter && !order.factura?.receptorNombre?.toLowerCase().includes(clienteFilter)) {
                    return false;
                }

                // Filtro por RFC
                if (rfcFilter && !order.clienteRFC.toLowerCase().includes(rfcFilter)) {
                    return false;
                }

                // Filtro por Texto General (búsqueda en múltiples campos)
                if (searchTerm) {
                    const matchesSearch =
                        order.folioOrden.toLowerCase().includes(searchTerm) ||
                        order.remision.folio.toLowerCase().includes(searchTerm) ||
                        order.clienteRFC.toLowerCase().includes(searchTerm) ||
                        (order.factura?.receptorNombre || '').toLowerCase().includes(searchTerm) ||
                        (order.folioFactura || '').toLowerCase().includes(searchTerm);

                    if (!matchesSearch) {
                        return false;
                    }
                }

                return true;
            });
        });

        // --- LÓGICA DE PAGINACIÓN ---
        this.totalOrdenesFiltradasSignal = computed(() => this.filteredOrdersSignal().length);

        this.ordersPaginadosSignal = computed(() => {
            const ordenesFiltradas = this.filteredOrdersSignal() || [];
            const pageEvent = this.pageEventSignal();
            const startIndex = pageEvent.pageIndex * pageEvent.pageSize;
            const endIndex = startIndex + pageEvent.pageSize;
            return ordenesFiltradas.slice(startIndex, endIndex);
        });
    }

    getHistoryDateForStatus(factura: Factura, targetStatus: string): string {
        if (!factura || !factura.statusHistory || factura.statusHistory.length === 0) {
            return 'Pendiente';
        }
        const historyEntry = factura.statusHistory
            .slice()
            .reverse()
            .find((entry: StatusHistory) => entry.estatus === targetStatus);

        if (historyEntry && historyEntry.fecha) {
            return this.datePipe.transform(historyEntry.fecha, 'dd/MM/yy HH:mm') || 'Fecha inválida';
        }
        return 'Pendiente';
    }

    getProcesadaStatusInfo(factura: Factura): { estatus: string; fecha: string } {
        if (!factura || !factura.statusHistory || factura.statusHistory.length === 0) {
            return { estatus: 'Pendiente', fecha: 'Pendiente' };
        }
        const estatusProcesado = this.getProcesadaGroupStatuses();
        const historyEntry = factura.statusHistory
            .slice()
            .reverse()
            .find((entry: StatusHistory) => estatusProcesado.includes(entry.estatus));

        const fechaFormateada = historyEntry ? this.datePipe.transform(historyEntry.fecha, 'dd/MM/yy HH:mm') || 'Fecha inválida' : 'Pendiente';

        return historyEntry
            ? { estatus: historyEntry.estatus, fecha: fechaFormateada }
            : { estatus: 'En Proceso', fecha: 'Pendiente' };
    }

    /**
     * Estatus que componen el paso "Procesada" (dashboardGroup === 'Procesada' del catálogo),
     * con fallback a la lista histórica.
     */
    getProcesadaGroupStatuses(): string[] {
        const statuses = this.statusTranslator.getStatuses()();
        const grouped = statuses
            .filter(s => !s.deleted && s.dashboardGroup === 'Procesada')
            .map(s => s.status);
        return grouped.length > 0 ? grouped : ['Facturada', 'Cancelada', 'Rechazada'];
    }

    /**
     * Pasos del stepper de factura derivados del catálogo:
     * - Un paso por cada dashboardGroup distinto (en orden de primer estatus del grupo)
     * - 'Procesada' se mantiene como paso agrupador (label corto)
     * Los pasos agrupan todos los estatus con order >= al primero de su grupo.
     */
    facturaStepperStepsSignal = computed(() => {
        const statuses = this.statusTranslator.getStatuses()()
            .filter(s => !s.deleted)
            .sort((a, b) => a.order - b.order);

        // Fallback si el catálogo no ha cargado
        if (statuses.length === 0) {
            return [
                { label: 'Val. Remisión', group: 'Validación de Remisión', statuses: ['Validación de Remisión'], color: '#1976d2' },
                { label: 'Procesada', group: 'Procesada', statuses: ['Facturada', 'Cancelada', 'Rechazada', 'Sin Factura', 'Prescrito'], color: '#7218d9' },
                { label: 'Tesorería', group: 'Tesorería', statuses: ['Tesorería'], color: '#9e9e9e' },
                { label: 'Contabilidad', group: 'Contabilidad', statuses: ['Contabilidad'], color: '#ff4081' },
                { label: 'Lista para Pago', group: 'Lista para Pago', statuses: ['Lista para Pago'], color: '#4caf50' },
                { label: 'Pagada', group: 'Pagada', statuses: ['Pagada'], color: '#198754' },
            ];
        }

        const steps: { label: string; group: string; statuses: string[]; color: string }[] = [];
        const groupOrder: string[] = [];

        for (const s of statuses) {
            const group = s.dashboardGroup || 'Otros';
            if (!groupOrder.includes(group)) {
                groupOrder.push(group);
                steps.push({
                    label: group === 'Procesada' ? 'Procesada' : (s.nombreSurtimiento || s.status),
                    group,
                    statuses: [],
                    color: s.color || '#1976d2',
                });
            }
            const step = steps.find(st => st.group === group);
            if (step) {
                step.statuses.push(s.status);
                if (!step.color) step.color = s.color || '#1976d2';
            }
        }

        return steps;
    });

    /**
     * Determina si un paso del stepper está activo para la factura dada.
     * Un paso está activo si el estatus actual pertenece a ese grupo O a un grupo posterior.
     */
    isStepperStepActive(factura: Factura | null, stepIndex: number): boolean {
        if (!factura) return false;
        const steps = this.facturaStepperStepsSignal();
        const currentGroup = this.statusTranslator.getDashboardGroup(factura.estatus) || 'Otros';
        const currentIndex = steps.findIndex(st => st.group === currentGroup);
        // Paso activo si ya se alcanzó o superó ese grupo (progreso lineal)
        return currentIndex >= stepIndex;
    }

    /**
     * Color del catálogo para el chip de estatus (undefined => usa el CSS por defecto).
     */
    getStatusColor(estatus: string | undefined): string | undefined {
        return this.statusTranslator.getColor(estatus);
    }

    /**
     * Icono numerado para el paso del stepper (looks_one..looks_9, luego check_circle).
     */
    getStepIcon(index: number): string {
        const numbered = ['looks_one', 'looks_two', 'looks_3', 'looks_4', 'looks_5', 'looks_6', 'looks_7', 'looks_8', 'looks_9'];
        return index < numbered.length ? numbered[index] : 'check_circle';
    }

    ngOnInit() {
        // Cargar órdenes y asegurar que las señales se inicialicen correctamente
        this.ordersService.loadSupplyOrders(true).subscribe({
            next: () => {
                this.customMessage.open({ title: 'Espere', type: 'info', message: 'Cargando órdenes de surtimiento...', duration: 500 });
                // Forzar detección de cambios para asegurar que las opciones de autocomplete estén disponibles
                setTimeout(() => {
                    this.cdr.detectChanges();
                }, 100);
            },
            error: (error) => {
                console.error('Error al cargar órdenes:', error);
                this.customMessage.open({ title: 'Error', type: 'error', message: 'No se pudieron cargar las órdenes', duration: 3000 });
            }
        });
    }

    calculateProgress(order: SupplyOrder): number {
        const facturaSteps = this.facturaStepsSignal();
        const totalSteps = Object.keys(this.remisionSteps).length + Object.keys(facturaSteps).length;
        let currentStep = 0;

        if (order.remision.estatus === 'Aprobada') {
            currentStep = this.remisionSteps['Aprobada'];
        } else if (order.remision.estatus === 'Validada') {
            currentStep = this.remisionSteps['Validada'];
            if (order.factura) {
                const facturaStep = facturaSteps[order.factura.estatus] || 0;
                currentStep += facturaStep;
            }
        }

        return (currentStep / totalSteps) * 100;
    }

    openAddDocsDialog(order: SupplyOrder): void {
        const dialogRef = this.dialog.open(AddDocsDialogComponent, {
            width: '600px',
            disableClose: true,
            data: { order: order },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.list) {
                this.customSnackBar.open('Guardando documentos...', 'info');
                this.ordersService.updateOrderDocuments(order.id, result.list).subscribe(() => {
                    this.customSnackBar.open('Documentos guardados correctamente.', 'success');
                });
            }
        });
    }

    ngAfterViewInit(): void {
        // Si usamos paginator viewChild, podemos sincronizar eventos extra si es necesario,
        // pero con (page)="pageEventSignal.set($event)" en el template suele ser suficiente para la reactividad.
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    goBack(): void {
        this.router.navigate(['/home']);
    }

    onCopyToClipboard(text: string): void {
        this.customSnackBar.open(`'${text}' copiado al portapapeles.`, 'info');
    }

    public forzarRefrescoCompleto(): void {
        this.customSnackBar.open('Actualizando datos desde el servidor...', 'info', 1.5);
        this.limpiarFiltros();

        this.ordersService.loadSupplyOrders(true).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.pageEventSignal.set({ pageIndex: 0, pageSize: 20, length: 0 });
                this.customSnackBar.open('Datos actualizados correctamente.', 'success');
            },
            error: (err) => {
                console.error('Error al forzar el refresco de datos:', err);
                this.customSnackBar.open('No se pudo actualizar la información.', 'error');
            }
        });
    }

    limpiarFiltros(): void {
        // Asegurarnos de que el spinner no se quede activado
        this.isFilteringSignal.set(false);

        // Limpiar el formulario completamente SIN emitir eventos
        this.filtroForm.reset({
            textoGeneral: '',
            cliente: '',
            rfc: '',
            estatus: ''
        }, { emitEvent: false });

        // Forzar actualización de los filtros inmediatamente
        this.filtrosValuesSignal.set({});

        // Resetear paginación
        if (this.pageEventSignal().pageIndex !== 0) {
            this.pageEventSignal.update(curr => ({ ...curr, pageIndex: 0 }));
        }

        // Forzar detección de cambios para limpiar los selects y autocompletes
        setTimeout(() => {
            this.cdr.detectChanges();
        }, 0);
    }

    private _filterList(value: string, list: string[]): string[] {
        const filterValue = value.toLowerCase();
        return list.filter(option => option.toLowerCase().includes(filterValue));
    }
}
